import { useState, useRef, useEffect, useMemo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import ALC from '@/assets/sidebar/ALC.svg';
import OW from '@/assets/sidebar/OW.svg';
import RA from '@/assets/sidebar/RA.svg';
import TI from '@/assets/sidebar/TI.svg';
import LS from '@/assets/sidebar/LS.svg';
import Home from '@/assets/sidebar/Home.svg';
import Alert from '@/assets/sidebar/Alert.svg';
import List from '@/assets/sidebar/List.svg';
import User from '@/assets/sidebar/User.svg';
import HomeHighlight from '@/assets/sidebar/Home-highlight.svg';
import AlertHighlight from '@/assets/sidebar/Alert-highlight.svg';
import ListHighlight from '@/assets/sidebar/List-highlight.svg';
import UserHighlight from '@/assets/sidebar/User-highlight.svg';
import ALCHighlight from '@/assets/sidebar/ALC-highlight.svg';
import OWighlight from '@/assets/sidebar/OW-highlight.svg';
import RAighlight from '@/assets/sidebar/RA-highlight.svg';
import TIighlight from '@/assets/sidebar/TI-highlight.svg';
import LSighlight from '@/assets/sidebar/LS-highlight.svg';
import { FaCircle } from 'react-icons/fa';
import './Sidebar.css';
import { buildWorstStatusMap } from '../utils/alertStatus';

const Sidebar = ({ slopeData, alertData, isMobile = false, isOpen = true, onClose }) => {
    const navigate = useNavigate();
    const { logout, user } = useAuth();
    const [activeMenu, setActiveMenu] = useState(null);
    const [activeSubIcon, setActiveSubIcon] = useState(null);
    const [isAccountOpen, setIsAccountOpen] = useState(false);
    const [showInstrumentPopup, setShowInstrumentPopup] = useState(false);
    const closeTimer = useRef(null);

    // Prevent body scroll when the sidebar or popup is open
    useEffect(() => {
        if (
            activeMenu ||
            activeSubIcon ||
            (isMobile && isOpen) ||
            (isMobile && showInstrumentPopup)
        ) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        // Restore scrolling on unmount
        return () => {
            document.body.style.overflow = '';
        };
    }, [activeMenu, activeSubIcon, isMobile, isOpen, showInstrumentPopup]);

    const closeMobileMenu = () => {
        if (isMobile && typeof onClose === 'function') {
            onClose();
        }
    };

    const statusByDeviceId = useMemo(
        () =>
            buildWorstStatusMap(alertData, {
                getKey: (alert) => alert?.device_id,
                onlyActive: true,
            }),
        [alertData]
    );

    function getDeviceStatusColor(deviceId) {
        const status = statusByDeviceId.get(deviceId);
        if (!status) return 'gray';

        switch (status) {
            case 'green':
                return '#00FF00'; // Normal
            case 'yellow':
                return '#FFFF00'; // Warning
            case 'orange':
                return '#FFA500'; // Caution
            case 'red':
                return '#FF0000'; // Critical
            default:
                return 'gray'; // Unknown
        }
    }

    function getDeviceId(deviceName, deviceType) {
        if (!slopeData) return null;

        const devices =
            deviceType === 'force'
                ? slopeData.force
                : deviceType === 'water_level'
                  ? slopeData.water_level
                  : slopeData.tilt;
        if (!Array.isArray(devices)) return null;

        const device = devices.find((item) => item.name === deviceName);
        return device ? device.id : null;
    }

    function extractNames(data) {
        const forceNames = Array.isArray(data.force)
            ? data.force.map((item) => item.name)
            : [];
        const waterLevelNames = Array.isArray(data.water_level)
            ? data.water_level.map((item) => item.name)
            : [];
        const tiltNames = Array.isArray(data.tilt)
            ? data.tilt.map((item) => item.name)
            : [];

        return {
            force: forceNames,
            water_level: waterLevelNames,
            tilt: tiltNames,
        };
    }

    var extractData = extractNames(slopeData);

    const topMenus = [
        {
            key: 'home',
            icon: Home,
            highlightIcon: HomeHighlight,
            path: '/',
            label: '首頁',
        },
        {
            key: 'warning',
            icon: Alert,
            highlightIcon: AlertHighlight,
            path: '/warning',
            label: '警告',
        },
        {
            key: 'list',
            icon: List,
            highlightIcon: ListHighlight,
            label: '列表',
            subIcons: [
                {
                    key: 'RA',
                    label: '雨量監測計',
                    icon: RA,
                    highlightIcon: RAighlight,
                    subMenu: [],
                },
                {
                    key: 'ALC',
                    label: '地錨荷重計',
                    icon: ALC,
                    highlightIcon: ALCHighlight,
                    subMenu: extractData.force,
                },
                {
                    key: 'LS',
                    label: '坍方偵測器',
                    icon: LS,
                    highlightIcon: LSighlight,
                    subMenu: [],
                },
                {
                    key: 'OW',
                    label: '水位觀測井',
                    icon: OW,
                    highlightIcon: OWighlight,
                    subMenu: extractData.water_level,
                },
                {
                    key: 'TI',
                    label: '傾斜計',
                    icon: TI,
                    highlightIcon: TIighlight,
                    subMenu: extractData.tilt,
                },
            ],
        },
    ];

    const roleValue = user?.role || user?.role_name || user?.roleName || '';
    const roleText = roleValue ? roleValue.toString() : '';
    const roleLower = roleText.toLowerCase();
    const isAdmin = roleLower.includes('admin');
    const canViewMonthlyReport =
        isAdmin || roleText.includes('陸島人員');
    const canViewAdminPage =
        isAdmin || roleText.includes('陸島人員');

    const bottomMenus = [
        {
            key: 'account',
            icon: User,
            highlightIcon: UserHighlight,
            label: '帳戶',
            subMenu: [
                '帳號資訊',
                ...(canViewAdminPage ? ['管理後台'] : []),
                ...(canViewMonthlyReport ? ['月報輔助工具'] : []),
                '登出',
            ],
        },
    ];

    const resetAll = () => {
        setActiveMenu(null);
        setActiveSubIcon(null);
        setIsAccountOpen(false);
    };

    const handleMenuClick = (item) => {
        clearTimeout(closeTimer.current);
        if (item.key === 'account') {
            setIsAccountOpen((prev) => !prev);
            return;
        }
        if (isMobile && item.key === 'list') {
            setShowInstrumentPopup(true);
            resetAll();
            closeMobileMenu();
            return;
        }
        if (activeMenu === item.key) {
            resetAll();
        } else if (item.subIcons || item.subMenu) {
            setActiveMenu(item.key);
            setActiveSubIcon(null);
        } else {
            resetAll();
            closeMobileMenu();
        }
    };

    const handleSubIconEnter = (subItem) => {
        clearTimeout(closeTimer.current);
        setActiveSubIcon(subItem.key);
    };

    const handleSubIconPanelEnter = () => clearTimeout(closeTimer.current);
    const handleSubIconPanelLeave = () => {
        closeTimer.current = setTimeout(() => resetAll(), 200);
    };

    const handleSubmenuEnter = () => clearTimeout(closeTimer.current);
    const handleSubmenuLeave = () => {
        closeTimer.current = setTimeout(() => resetAll(), 200);
    };

    const handleAccountAction = async (action) => {
        resetAll();
        setIsAccountOpen(false);
        if (action === '帳號資訊') {
            navigate('/account');
            closeMobileMenu();
            return;
        }
        if (action === '月報輔助工具') {
            navigate('/monthly-report');
            closeMobileMenu();
            return;
        }
        if (action === '管理後台') {
            navigate('/admin');
            closeMobileMenu();
            return;
        }
        if (action === '登出') {
            await logout();
            navigate('/login');
            closeMobileMenu();
            return;
        }
        alert(action);
        closeMobileMenu();
    };

    const handleDeviceSelect = (deviceName) => {
        if (!deviceName) return;
        resetAll();
        navigate(`/device/${deviceName}`);
        closeMobileMenu();
        setShowInstrumentPopup(false);
    };

    const renderMenu = (menus) =>
        menus.map((item) => (
            <li
                key={item.key}
                className="sidebar__item"
                onClick={() => handleMenuClick(item)}
            >
                {item.path ? (
                    <NavLink to={item.path} className="sidebar__link">
                        <img src={item.icon} className="submenu-icon" alt={item.key} />
                        <span className="sidebar__label">{item.label}</span>
                    </NavLink>
                ) : (
                    <div className="sidebar__link">
                        <img src={item.icon} className="submenu-icon" alt={item.key} />
                        <span className="sidebar__label">{item.label}</span>
                    </div>
                )}
            </li>
        ));

    const activeSub =
        activeMenu &&
        topMenus
            .find((m) => m.key === activeMenu)
            ?.subIcons?.find((s) => s.key === activeSubIcon);

    const listMenu = topMenus.find((menu) => menu.key === 'list');
    const instrumentGroups = listMenu?.subIcons || [];
    const closeInstrumentPopup = () => setShowInstrumentPopup(false);

    const wrapperClassNames = [
        'sidebar-wrapper',
        isMobile ? 'sidebar-wrapper--mobile' : '',
        isMobile && isOpen ? 'sidebar-wrapper--mobile-open' : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <>
            {isMobile && (
                <div
                    className={`sidebar-mobile__backdrop${
                        isOpen ? ' sidebar-mobile__backdrop--visible' : ''
                    }`}
                    onClick={() => {
                        resetAll();
                        closeMobileMenu();
                    }}
                />
            )}
            <div className={wrapperClassNames} aria-hidden={isMobile && !isOpen}>
                <div className="sidebar-container">
                    <aside className="sidebar">
                        <ul className="sidebar__list">{renderMenu(topMenus)}</ul>
                        <ul className="sidebar__bottom">{renderMenu(bottomMenus)}</ul>
                        {isAccountOpen && bottomMenus.length > 0 && (
                            <div
                                className={`account-dropdown${
                                    isMobile ? ' account-dropdown--mobile' : ''
                                }`}
                            >
                                {bottomMenus[0].subMenu.map((option) => (
                                    <button
                                        type="button"
                                        key={option}
                                        className="account-dropdown__item"
                                        onClick={() => handleAccountAction(option)}
                                    >
                                        {option}
                                    </button>
                                ))}
                            </div>
                        )}
                    </aside>
                </div>

                {/* SubIcon Panel */}
                {!isMobile &&
                    activeMenu &&
                    topMenus.find((m) => m.key === activeMenu)?.subIcons && (
                        <div
                            className="subicon-panel"
                            onMouseEnter={handleSubIconPanelEnter}
                            onMouseLeave={handleSubIconPanelLeave}
                        >
                            {topMenus
                                .find((m) => m.key === activeMenu)
                                .subIcons.map((sub) => {
                                    const isActive = sub.key === activeSubIcon;
                                    return (
                                        <div
                                            key={sub.key}
                                            className="subicon-item"
                                            onMouseEnter={() => handleSubIconEnter(sub)}
                                            onTouchStart={() => handleSubIconEnter(sub)}
                                        >
                                            <img
                                                src={
                                                    isActive
                                                        ? sub.highlightIcon
                                                        : sub.icon
                                                }
                                                alt={sub.key}
                                                className="submenu-icon"
                                            />
                                            <span className="subicon-label">
                                                {sub.label}
                                            </span>
                                        </div>
                                    );
                                })}
                        </div>
                    )}

                {/* Submenu Panel for subicon */}
                {!isMobile && activeSubIcon && activeSub && (
                    <div
                        className="submenu-panel submenu-panel--subicon"
                        onMouseEnter={handleSubmenuEnter}
                        onMouseLeave={handleSubmenuLeave}
                    >
                        <div className="submenu-header">
                            <span className="submenu-title">{activeSub.label}</span>
                            <svg
                                className="submenu-divider"
                                width="100%"
                                height="2"
                                viewBox="0 0 100 2"
                                preserveAspectRatio="none"
                            >
                                <line
                                    x1="0"
                                    y1="1"
                                    x2="100"
                                    y2="1"
                                    stroke="#00C7BE"
                                    strokeWidth="2"
                                />
                            </svg>
                        </div>
                        {activeSub.subMenu.map((subItem, idx) => {
                            const deviceType =
                                activeSub.key === 'ALC'
                                    ? 'force'
                                    : activeSub.key === 'OW'
                                      ? 'water_level'
                                      : activeSub.key === 'TI'
                                        ? 'tilt'
                                        : null;
                            const deviceId = getDeviceId(subItem, deviceType);
                            const statusColor = deviceId
                                ? getDeviceStatusColor(deviceId)
                                : 'gray';

                            return (
                                <div
                                    key={idx}
                                    className="submenu-item"
                                    onClick={() => {
                                        resetAll();
                                        navigate(`/device/${subItem}`);
                                        closeMobileMenu();
                                    }}
                                >
                                    <FaCircle
                                        className="status-dot"
                                        color={statusColor}
                                    />
                                    {subItem}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {isMobile && showInstrumentPopup && (
                <div className="instrument-modal">
                    <div
                        className="instrument-modal__backdrop"
                        onClick={closeInstrumentPopup}
                    />
                    <div className="instrument-modal__panel">
                        <div className="instrument-modal__header">
                            <span>儀器列表</span>
                            <button
                                type="button"
                                className="instrument-modal__close"
                                onClick={closeInstrumentPopup}
                            >
                                關閉
                            </button>
                        </div>
                        <div className="instrument-modal__body">
                            {instrumentGroups.map((group) => (
                                <section key={group.key} className="instrument-group">
                                    <div className="instrument-group__title">
                                        {group.label}
                                    </div>
                                    <div className="instrument-group__devices">
                                        {Array.isArray(group.subMenu) &&
                                        group.subMenu.length ? (
                                            group.subMenu.map((deviceName) => {
                                                const deviceType =
                                                    group.key === 'ALC'
                                                        ? 'force'
                                                        : group.key === 'OW'
                                                          ? 'water_level'
                                                          : group.key === 'TI'
                                                            ? 'tilt'
                                                            : null;
                                                const deviceId = getDeviceId(
                                                    deviceName,
                                                    deviceType
                                                );
                                                const statusColor = deviceId
                                                    ? getDeviceStatusColor(deviceId)
                                                    : '#94a3b8';
                                                return (
                                                    <button
                                                        type="button"
                                                        key={deviceName}
                                                        className="instrument-device"
                                                        onClick={() =>
                                                            handleDeviceSelect(deviceName)
                                                        }
                                                    >
                                                        <FaCircle
                                                            className="instrument-device__status"
                                                            color={statusColor}
                                                        />
                                                        <span className="instrument-device__name">
                                                            {deviceName}
                                                        </span>
                                                    </button>
                                                );
                                            })
                                        ) : (
                                            <div className="instrument-group__empty">
                                                尚無資料
                                            </div>
                                        )}
                                    </div>
                                </section>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Sidebar;
